import { Box, Chip } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

const HEADER_HEIGHT = 80

export const ApplicationBody = styled(Box)`
  display: flex;
  align-items: stretch;
  width: 100%;
  height: calc(100vh - ${HEADER_HEIGHT}px);
  background: ${theme.colors.pageBackground};
`

/* An even split, as the inbound setup page has, so the instructions never take the larger half. */
export const ApplicationFormColumn = styled(Box)`
  flex: 1 1 50%;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  min-width: 0;
  padding: ${theme.spacing.medium}px;
  border-right: 1px solid ${theme.colors.strokeSecondary};
  overflow-x: hidden;
  overflow-y: auto;

  .section-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.header};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 36px;
    letter-spacing: 0.1px;
  }

  .section-description {
    margin-top: ${theme.spacing.half}px;
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
  }
`

/* The step-one body: how the connection is made, whom to sign in as, and what will be asked for. */
export const ConnectStepWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg}px;
  width: 100%;

  .block-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
    letter-spacing: 0.1px;
  }

  .block-text {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
  }

  .auth-method {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.half}px;
  }

  .auth-method-value {
    color: ${theme.colors.cta};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
    letter-spacing: 0.1px;
  }

  .service-account {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.half}px;
    padding: ${theme.spacing.md}px ${theme.spacing.small}px;
    border-left: 2px solid ${theme.colors.cta};
    border-radius: ${theme.attributes.borderRadius.secondary};
    background: ${theme.colors.cardFillSecondary};
  }

  .permissions {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.xSmall}px;
  }

  .permission-item {
    display: flex;
    align-items: flex-start;
    gap: ${theme.spacing.xSmall}px;
  }

  .permission-bullet {
    flex: 0 0 ${theme.spacing.half}px;
    width: ${theme.spacing.half}px;
    height: ${theme.spacing.half}px;
    margin-top: ${theme.spacing.xsm}px;
    border-radius: ${theme.attributes.borderRadius.circle};
    background: ${theme.colors.cta};
  }

  .connect-form {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.lg}px;
    width: 100%;
  }

  .retry-block {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.lg}px;
    width: 100%;
  }

  .retry-reason {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.half}px;
    padding: ${theme.spacing.md}px ${theme.spacing.small}px;
    border-left: 2px solid ${theme.colors.errorLight};
    border-radius: ${theme.attributes.borderRadius.secondary};
    background: ${theme.colors.cardFillSecondary};
  }

  .page-error {
    color: ${theme.colors.errorLight};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
  }
`

/* The step-two body: what the approval produced, read-only, above the editable name. */
export const ConfigureStepWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg}px;
  width: 100%;

  .success-banner {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.half}px;
    padding: ${theme.spacing.md}px ${theme.spacing.small}px;
    border-left: 2px solid ${theme.colors.statusValueGood};
    border-radius: ${theme.attributes.borderRadius.secondary};
    background: ${theme.colors.cardFillSecondary};
  }

  .banner-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
    letter-spacing: 0.1px;
  }

  .banner-text,
  .card-address {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
  }

  .connection-card {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.small}px;
    padding: ${theme.spacing.small}px;
    border: 1px solid ${theme.colors.strokeSecondary};
    border-radius: ${theme.attributes.borderRadius.tertiary};
  }

  .card-block {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.half}px;
  }

  .card-label {
    color: ${theme.colors.textCaption};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 16px;
    letter-spacing: 0.4px;
  }

  .card-value {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
    letter-spacing: 0.1px;
  }

  .scope-chips {
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing.xSmall}px;
  }
`

/* One granted permission, in the manner of the category chips on the integration cards. */
export const ScopeChip = styled(Chip)`
  height: 28px;
  width: fit-content;
  min-width: 0;
  padding: ${theme.spacing.half}px ${theme.spacing.small}px;
  border-radius: 6px;
  background: ${theme.colors.devicesRowHoverBg};
  color: ${theme.colors.devicesSecondaryText};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.medium};
  font-style: normal;
  font-weight: ${theme.typography.fontWeight.extraThick};
  line-height: 20px;
  letter-spacing: 0.1px;
`

/* The step's own action, shaped like the inbound page's Test Connection button so the two setup
 * flows offer the same thing to click. `sx` rather than a class, because that is what outranks
 * the shared button's own styling, and it is how the inbound button does it. */
export const primaryActionSx = {
  alignSelf: 'flex-start',
  minWidth: '177px',
  height: '48px !important',
  borderRadius: `${theme.attributes.borderRadius.tertiary} !important`,
  boxShadow: 'none',
  color: `${theme.colors.surfaceContainerBackground} !important`,
  fontFamily: theme.typography.fontFamily.primary,
  fontSize: `${theme.typography.fontSize.large} !important`,
  fontStyle: 'normal !important',
  fontWeight: `${theme.typography.fontWeight.bold} !important`,
  lineHeight: '22px !important',

  '.MuiCircularProgress-root': {
    color: theme.colors.cta
  },

  '&:hover': {
    background: `${theme.colors.buttonHollowHover} !important`,
    color: `${theme.colors.primaryDark} !important`
  },

  '&:focus, &.Mui-focused': {
    background: `${theme.colors.btnTertiaryBg} !important`,
    color: `${theme.colors.cta} !important`
  }
}

export const ApplicationRightPane = styled(Box)`
  display: flex;
  flex: 1 1 50%;
  flex-direction: column;
  min-width: 0;
  padding: ${theme.spacing.medium}px;
  overflow-y: auto;
`

export const UnknownIntegrationWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.small}px;
  width: 100%;
  height: 100vh;
  background: ${theme.colors.pageBackground};

  .unknown-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.header};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 28px;
    letter-spacing: 0.1px;
  }

  .unknown-text {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
  }
`
